<template>
  <div class="route-validities">
    <div class="route-validities__toolbar">
      <a-input
        v-model="searchValue"
        placeholder="Busque pela data da vigência"
        class="route-validities__search"
        allow-clear
        @change="onSearchChange"
      >
        <a-icon slot="prefix" type="search" style="color: rgba(0, 0, 0, 0.25)" />
      </a-input>

      <a-radio-group
        v-model="activeStatus"
        class="route-validities__tabs"
        @change="onStatusChange"
      >
        <a-radio-button
          v-for="tab in tabsWithCounts"
          :key="tab.value"
          :value="tab.value"
        >
          {{ tab.label }}
          <span class="route-validities__tab-count">{{ tab.count }}</span>
        </a-radio-button>
      </a-radio-group>

      <FreightTableActionsMenu
        v-if="menuActions.length"
        class="route-validities__actions-btn"
        :items="menuActions"
        @select="onMenuAction"
      />
    </div>

    <a-spin v-if="loading" class="route-validities__spin" />

    <a-empty
      v-else-if="!items.length"
      class="route-validities__empty"
      :description="emptyText"
    />

    <template v-else>
      <div
        v-for="validity in visibleItems"
        :key="validity.validityId"
        class="route-validities__row"
      >
        <a-checkbox
          v-if="bulkActions.length"
          :checked="isSelected(validity)"
          :disabled="isCancelled(validity)"
          @change="onToggleSelection(validity)"
        />

        <a-icon type="calendar" class="route-validities__row-icon" />

        <span class="route-validities__dates">
          <span class="route-validities__label">Inicio:</span>
          {{ validity.inicio }}
          <span class="route-validities__separator">•</span>
          <span class="route-validities__label">Fim:</span>
          {{ validity.fim }}
        </span>

        <span v-if="validity.revisadoPor" class="route-validities__reviewer">
          <span class="route-validities__separator">•</span>
          <span class="route-validities__label">
            {{ isApproved(validity) ? "Aprovado por:" : "Cancelado por:" }}
          </span>
          {{ validity.revisadoPor }} em {{ validity.revisadoEm }}
        </span>

        <div class="route-validities__row-actions">
          <template v-if="isPending(validity)">
            <a-button
              v-if="canCancel"
              class="route-validities__cancel-btn"
              @click="$emit('review', { validityIds: [validity.validityId], approve: false })"
            >
              Cancelar
            </a-button>
            <a-button
              v-if="canApprove"
              type="primary"
              class="route-validities__approve-btn"
              @click="$emit('review', { validityIds: [validity.validityId], approve: true })"
            >
              Aprovar
            </a-button>
          </template>

          <StatusBadge
            v-else
            :label="validity.status"
            :active="isApproved(validity)"
          />

          <a-button
            class="route-validities__icon-btn"
            @click="$emit('open-details', validity)"
          >
            <a-icon :type="isOpen(validity) ? 'left' : 'right'" />
          </a-button>
        </div>
      </div>

      <a
        v-if="hasMore"
        class="route-validities__show-all"
        href="#"
        @click.prevent="showAll = !showAll"
      >
        {{ showAll ? "Exibir menos" : "Exibir todos" }}
        <a-icon :type="showAll ? 'up' : 'down'" />
      </a>
    </template>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import StatusBadge from "@/shared/components/StatusBadge.vue";
import FreightTableActionsMenu from "../FreightTableActionsMenu.vue";
import {
  FREIGHT_VALIDITY_STATUS,
  FREIGHT_VALIDITY_TABS,
} from "../../constants/freightValidityStatus";

const COLLAPSED_LIMIT = 4;

export default {
  name: "FreightTableRouteValidities",
  components: { StatusBadge, FreightTableActionsMenu },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    canApprove: {
      type: Boolean,
      default: true,
    },
    canCancel: {
      type: Boolean,
      default: true,
    },
    openValidityId: {
      type: Number,
      default: null,
    },
    counts: {
      type: Object,
      default: () => ({}),
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      statusTabs: FREIGHT_VALIDITY_TABS,
      activeStatus: FREIGHT_VALIDITY_STATUS.PENDENTE,
      searchValue: "",
      showAll: false,
    };
  },
  computed: {
    tabsWithCounts() {
      return this.statusTabs.map((tab) => ({
        ...tab,
        count: this.counts[tab.value] || 0,
      }));
    },
    activeTabLabel() {
      const tab = this.statusTabs.filter(
        (item) => item.value === this.activeStatus,
      )[0];
      return tab ? tab.label.toLowerCase() : "";
    },
    emptyText() {
      if (String(this.searchValue || "").trim()) {
        return "Nenhuma vigência encontrada para essa busca.";
      }

      return this.activeTabLabel
        ? `Não há vigências ${this.activeTabLabel} no momento`
        : "Nenhuma vigência encontrada.";
    },
    visibleItems() {
      return this.showAll ? this.items : this.items.slice(0, COLLAPSED_LIMIT);
    },
    hasMore() {
      return this.items.length > COLLAPSED_LIMIT;
    },
    hasSelection() {
      return this.selectedIds.length > 0;
    },
    bulkActions() {
      const actions = [];

      if (this.canApprove) {
        actions.push({
          key: "approve",
          label: "Aprovar selecionadas",
          icon: "check-circle",
          disabled: !this.hasSelection,
        });
      }

      if (this.canCancel) {
        actions.push({
          key: "cancel",
          label: "Cancelar selecionadas",
          icon: "minus-circle",
          danger: true,
          disabled: !this.hasSelection,
        });
      }

      return actions;
    },
    menuActions() {
      if (!this.canCreate) return this.bulkActions;

      return [
        { key: "create", label: "Nova vigência", icon: "plus" },
      ].concat(this.bulkActions);
    },
  },
  created() {
    this.debouncedSearch = debounce(this.emitFilterChange, 500);
  },
  beforeDestroy() {
    this.debouncedSearch.cancel();
  },
  methods: {
    isPending(validity) {
      return validity.statusValor === FREIGHT_VALIDITY_STATUS.PENDENTE;
    },
    isApproved(validity) {
      return validity.statusValor === FREIGHT_VALIDITY_STATUS.APROVADA;
    },
    isOpen(validity) {
      return (
        this.openValidityId !== null &&
        validity.validityId === this.openValidityId
      );
    },
    isCancelled(validity) {
      return validity.statusValor === FREIGHT_VALIDITY_STATUS.CANCELADA;
    },
    isSelected(validity) {
      return this.selectedIds.indexOf(validity.validityId) !== -1;
    },
    onToggleSelection(validity) {
      this.$emit("toggle-selection", validity.validityId);
    },
    onSearchChange() {
      this.debouncedSearch();
    },
    onStatusChange() {
      this.showAll = false;
      this.emitFilterChange();
    },
    emitFilterChange() {
      this.$emit("filter-change", {
        status: this.activeStatus,
        search: this.searchValue,
      });
    },
    onMenuAction(key) {
      if (key === "create") {
        this.$emit("create");
        return;
      }

      this.onBulkAction(key);
    },
    onBulkAction(key) {
      if (!this.hasSelection) return;

      this.$emit("review", {
        validityIds: this.selectedIds,
        approve: key === "approve",
      });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.route-validities {
  &__toolbar {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__search {
    flex: 0 1 @details-search-width;
    width: @details-search-width;
    min-width: 100px;

    /deep/ .ant-input {
      border-radius: 6px;
    }
  }

  &__tab-count {
    display: inline-block;
    min-width: 18px;
    margin-left: 6px;
    padding: 0 5px;
    border-radius: 9px;
    background: @color-bg-subtle;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
    color: @color-text-medium;
  }

  &__tabs {
    flex: none;
    background-color: @color-bg-subtle;
    padding: 4px;
    border-radius: 8px;
    display: inline-flex;
    white-space: nowrap;

    /deep/ .ant-radio-button-wrapper {
      background: transparent;
      border: none;
      box-shadow: none !important;
      color: @color-text-light;
      border-radius: 6px;
      height: 28px;
      line-height: 26px;
      display: flex;
      align-items: center;

      &:not(:first-child)::before {
        display: none;
      }
    }

    /deep/ .ant-radio-button-wrapper-checked {
      background-color: #fff;
      color: @color-text-dark;
      font-weight: 500;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
    }
  }

  &__actions-btn {
    margin-left: auto;
  }

  &__spin,
  &__empty {
    display: block;
    padding: 24px;
    text-align: center;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid @color-bg-subtle;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 8px;
  }

  &__row-icon {
    color: @color-primary;
  }

  &__dates,
  &__reviewer {
    font-size: 14px;
    color: @color-text-dark;
    white-space: nowrap;
  }

  &__label {
    color: @color-text-light;
  }

  &__separator {
    color: @color-text-muted;
    margin: 0 6px;
  }

  &__row-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  &__cancel-btn,
  &__approve-btn {
    border-radius: 6px;
    height: 32px;
  }

  &__approve-btn {
    background-color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 6px;
    color: @color-primary;
  }

  &__show-all {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: @color-primary;
    font-weight: 500;
    margin-top: 8px;

    &:hover,
    &:focus {
      color: @color-primary-hover;
      text-decoration: none;
    }
  }
}
</style>
